import { isEmpty } from 'lodash'
import ApiHelper from '../../helpers/ApiHelper'

export class ApiProduct {

    getBaseURL() {
        return ApiHelper.getBaseURL()
    }

    async trackMetaEvent(event: any) {
        return await ApiHelper.post('/v1/meta/events', event)
    }

    async signup(user: any) {
        return await ApiHelper.post(`/v1/user/signup`, user)
    }

    async verifyAccount(data: any) {
        return ApiHelper.post(`/v1/user/verify`, data)
    }

    async login(login: any) {
        return await ApiHelper.post(`/v1/user/login`, login)
    }

    async passwordRecover(data: any) {
        return await ApiHelper.post(`/v1/user/password-recover`, data)
    }

    async validatePasswordRecoverToken(token: string) {
        return await ApiHelper.get(`/v1/user/validate-password-recover-token/${token}`)
    }

    async confirmPasswordRecover(data: any) {
        return await ApiHelper.post(`/v1/user/confirm-password-recover`, data)
    }

    async updatePassword(data: any) {
        return await ApiHelper.put(`/v1/user/update-password`, data)
    }

    async getUsers() {
        return await ApiHelper.get(`/v1/user/get`)
    }

    async getUserById(userId: string) {
        return await ApiHelper.get(`/v1/user/get-by-id/${userId}`)
    }

    async getUserAddresses() {
        return await ApiHelper.get(`/v1/user/get-addresses`)
    }

    async getLastAddressUsed() {
        return await ApiHelper.get(`/v1/user/get-last-used-address`)
    }

    async createUser(user: any) {
        return await ApiHelper.post(`/v1/user/save`, user)
    }

    async updateUser(user: any) {
        return await ApiHelper.put(`/v1/user/update`, user)
    }

    async deleteUser(id: string) {
        return await ApiHelper.delete(`/v1/user/delete/${id}`)
    }

    async uploadUserAvatar(data: any) {
        return await ApiHelper.postWithFiles(`/v1/user/upload-avatar`, data)
    }

    async confirmAccount(data: any) {
        return await ApiHelper.post(`/v1/user/confirm-account`, data)
    }

    /// ROLES
    async getRoles() {
        return await ApiHelper.get(`/v1/role/get`)
    }

    async getRole(roleId: string) {
        return await ApiHelper.get(`/v1/role/get-by-id/${roleId}`)
    }

    async createRole(role: any) {
        return await ApiHelper.post(`/v1/role/save`, role)
    }

    async updateRole(role: any) {
        return await ApiHelper.put(`/v1/role/update`, role)
    }

    async deleteRole(id: string) {
        return await ApiHelper.delete(`/v1/role/delete/${id}`)
    }

    /// BRANDS
    async getBrands() {
        return await ApiHelper.get(`/v1/brand/get`)
    }

    async getBrandById(id: string) {
        return await ApiHelper.get(`/v1/brand/get-by-id/${id}`)
    }

    async createBrand(data: any) {
        return await ApiHelper.post(`/v1/brand/save`, data)
    }

    async updateBrand(data: any) {
        return await ApiHelper.put(`/v1/brand/update`, data)
    }

    async deleteBrand(id: string) {
        return await ApiHelper.delete(`/v1/brand/delete/${id}`)
    }

    async uploadBrandImage(data: any) {
        return await ApiHelper.postWithFiles(`/v1/brand/upload`, data)
    }

    /// BRANCHES
    async getBranches() {
        return await ApiHelper.get(`/v1/branch/get`)
    }

    async getBranch(id: string) {
        return await ApiHelper.get(`/v1/branch/get-by-id/${id}`)
    }

    async createBranch(data: any) {
        return await ApiHelper.post(`/v1/branch/save`, data)
    }

    async updateBranch(data: any) {
        return await ApiHelper.put(`/v1/branch/update`, data)
    }

    async deleteBranch(id: string) {
        return await ApiHelper.delete(`/v1/branch/delete/${id}`)
    }

    // CUSTOMER ADDRESS
    async addCustomerAddress(data: any) {
        return await ApiHelper.post(`/v1/customer/address/add`, data)
    }

    async getAddressByCustomerId(id: string) {
        return await ApiHelper.get(`/v1/customer/address/get-by-customer-id/${id}`)
    }

    async getAddressById(id: string) {
        return await ApiHelper.get(`/v1/customer/address/get-by-id/${id}`)
    }

    async deleteCustomerAddress(id: string) {
        return await ApiHelper.delete(`/v1/customer/address/delete/${id}`)
    }

    async updateCustomerAddress(data: any) {
        return await ApiHelper.put(`/v1/customer/address/update`, data)
    }

    /// PRODUCTS
    async getProducts(offset: number, limit: number, filter: any) {
        let moreParams = '&'
        if (!isEmpty(filter)) {
            for (const f of filter) {
                moreParams += `${f.columnName}=${f.value}`
            }
        }

        return await ApiHelper.get(`/v1/product/get?offset=${offset}&limit=${limit}${moreParams}`)
    }

    // Get trending products
    getTrendingProducts (limit: number = 25, days: number = 30) {
        const daysParam = days ? `&days=${days}` : ''

        return ApiHelper.get(`/v1/product/get-trending?limit=${limit}${daysParam}`)
    }

    // Get random products
    getRandomProducts(limit: number = 10) {
        return ApiHelper.get(`/v1/product/random?limit=${limit}`)
    }

    // Get similar products
    getSimilarProducts(productId: string, limit: number = 10) {
        return ApiHelper.get(`/v1/product/get-similar/${productId}?limit=${limit}`)
    }

    // Get products by category
    getProductsByCategory(categoryId: string) {
        return ApiHelper.get(`/v1/product/get-by-category/${categoryId}`)
    }

    // Get products by subcategory
    getProductsBySubcategory(subcategoryId: string) {
        return ApiHelper.get(`/v1/product/get-by-subcategory/${subcategoryId}`)
    }

    getTrendingCategories(limit: number = 10, days?: number) {
        const daysParam = days ? `&days=${days}` : ''
        return ApiHelper.get(`/v1/category/trending?limit=${limit}${daysParam}`)
    }

    // Get trending subcategories
    getTrendingSubcategories(limit: number = 10, days?: number, categoryId?: string) {
        const daysParam = days ? `&days=${days}` : ''
        const categoryParam = categoryId ? `&categoryId=${categoryId}` : ''
        return ApiHelper.get(`/v1/subcategory/trending?limit=${limit}${daysParam}${categoryParam}`)
    }

    getSimilarProductsFromCart(productIds: string[], limit: number = 10) {
        const idsParam = productIds.join(',')
        return ApiHelper.get(`/v1/product/get-similar-by-cart?productIds=${idsParam}&limit=${limit}`)
    }

    async getSimilarProductsFromSaleId(saleId: string | undefined) {
        return ApiHelper.get(`/v1/product/get-similar-products/${saleId}`)
    }

    async fetchDefaultPriceList() {
        return await ApiHelper.get('/v1/price-types/get-default')
    }

    async getPopularProducts() {
        return await ApiHelper.get(`/v1/product/get-popular`)
    }

    async searchProducts(keyword: string, offset: number = 0, limit: number = 30) {
        return await ApiHelper.get(`/v1/product/search/${encodeURIComponent(keyword)}?offset=${offset}&limit=${limit}`)
    }

    async searchVariantsBySupplierId(keyword: string, supplierId: string) {
        return await ApiHelper.get(`/v1/product/search-variants-by-supplier/${keyword}/${supplierId}`)
    }

    async getProductsByCategorySlug(slug: string | (string | null)[] | null, offset: number = 0, limit: number = 30) {
        return await ApiHelper.get(`/v1/product/get-by-category/${slug}?offset=${offset}&limit=${limit}`)
    }

    async getProductsBySubCategorySlug(slug: string | (string | null)[] | null, offset: number = 0, limit: number = 30) {
        return await ApiHelper.get(`/v1/product/get-by-subcategory/${slug}?offset=${offset}&limit=${limit}`)
    }

    // eslint-disable-next-line
    async searchVariants(keyword: string) {
        return await ApiHelper.get(`/v1/product/search-variants/${keyword}`)
    }

    async getProductById(id: string, includeVariants: boolean) {
        const query = includeVariants ? `?includeVariants=true` : ''
        return await ApiHelper.get(`/v1/product/get-by-id/${id}${query}`)
    }

    async getProductBySlug(slug: string | (string | null)[] | null) {
        return await ApiHelper.get(`/v1/product/get-by-slug/${slug}`)
    }

    async getProductSampleImages() {
        return await ApiHelper.get(`/v1/product-sample-images`)
    }

    async createProduct(data: any) {
        return await ApiHelper.post(`/v1/product/backoffice/save`, data)
    }

    async updateProduct(data: any) {
        return await ApiHelper.post(`/v1/product/backoffice/update`, data)
    }

    async deleteProduct(id: string) {
        return await ApiHelper.delete(`/v1/product/backoffice/delete/${id}`)
    }

    async exportProducts(filters: any) {
        return await ApiHelper.post(`/v1/product/export`, filters)
    }

    async exportProductsForPriceUpdate(data: any) {
        return await ApiHelper.post(`/v1/product/export-product-for-pricing-update`, data)
    }

    async exportProductsForStockUpdate(data: any) {
        return await ApiHelper.post(`/v1/product/export-product-for-stock-update`, data)
    }

    async uploadProductImage(data: any) {
        return await ApiHelper.postWithFiles(`/v1/product/backoffice/upload`, data)
    }

    async addProductVariant(data: any) {
        return await ApiHelper.post(`/v1/product/backoffice/add-variant`, data)
    }

    async deleteVariant(id: string) {
        return await ApiHelper.delete(`/v1/product/backoffice/delete-variant/${id}`)
    }

    async editVariant(data: any) {
        return await ApiHelper.put(`/v1/product/edit-variant`, data)
    }

    async deleteProductImage(productId: string, fileName: string) {
        return await ApiHelper.delete(`/v1/product/backoffice/images/delete/${productId}/${encodeURIComponent(fileName)}`)
    }

    async setDefaultImage(data: any) {
        return await ApiHelper.put(`/v1/product/images/set-default`, data)
    }

    async getProductVariant(id: string) {
        return await ApiHelper.get(`/v1/product/backoffice/variants/get/${id}`)
    }

    async getProductGallery(productId: string) {
        return await ApiHelper.get(`/v1/product/gallery/get/${productId}`)
    }

    async getProductCodes() {
        return await ApiHelper.get(`/v1/product/get-product-codes`)
    }

    /// CATEGORIES
    async getCategories(type?: 'CATALOG' | 'ARTIST') {
        const query = type ? `?type=${type}` : ''
        return await ApiHelper.get(`/v1/category/get${query}`)
    }

    async getCategoryById(id: string) {
        return await ApiHelper.get(`/v1/category/get-by-id/${id}`)
    }

    async getCategoryBySlug(slug: string | (string | null)[] | null) {
        return await ApiHelper.get(`/v1/category/get-by-slug/${slug}`)
    }

    async getCategoriesByFamilyId(id: string) {
        return await ApiHelper.get(`/v1/category/get-by-family/${id}`)
    }

    async getCurrencies() {
        return await ApiHelper.get(`/v1/bank/get-currencies`)
    }

    /// SUBCATEGORIES
    async getSubcategories() {
        return await ApiHelper.get(`/v1/subcategory/get`)
    }

    async getSubcategoryById(id: string | (string | null)[] | null) {
        return await ApiHelper.get(`/v1/subcategory/get-by-id/${id}`)
    }

    async getSubcategoryBySlug(slug: string | (string | null)[] | null) {
        return ApiHelper.get(`/v1/subcategory/get-by-slug/${slug}`)
    }

    async getSubcategoriesByCategoryId(id: string) {
        return await ApiHelper.get(`/v1/subcategory/get-by-category/${id}`)
    }

    // Sales
    async getSaleById(saleId: string) {
        return await ApiHelper.get(`/v1/sales/get-by-id/${saleId}`)
    }

    async saveSale(sale: any) {
        return await ApiHelper.post(`/v1/sales/save`, sale)
    }

    async fetchLastFolio() {
        return await ApiHelper.get(`/v1/sales/get-last-folio`)
    }

    async fetchNextFolio() {
        return await ApiHelper.get(`/v1/sales/get-next-folio`)
    }

    async getSales() {
        return await ApiHelper.get(`/v1/sales/get`)
    }

    async getSalesByUserId(userId: string) {
        return ApiHelper.get(`/v1/sales/get-by-user-id/${userId}`)
    }

    /// DISCOUNTS
    async getActiveDiscounts(type?: 'store' | 'automatic') {
        const query = type ? `?type=${type}` : ''
        return await ApiHelper.get(`/v1/sales/active-discounts${query}`)
    }

    async validateDiscountCode(code: string, lineItems: any[]) {
        return await ApiHelper.post(`/v1/sales/validate-discount-code`, { code, lineItems })
    }

    async calculatePricing(lineItems: any[], discountCode?: string, userId?: string, shippingAddress?: any) {
        return await ApiHelper.post(`/v1/pricing/cart`, { lineItems, discountCode, userId, shippingAddress })
    }

    async snapshotCartRecovery(payload: any) {
        return await ApiHelper.post(`/v1/cart-recovery/snapshot`, payload)
    }

    /// CHECKOUT
    async CreatePaymentIntent(payload: {
        lineItems: any[],
        amount: number,
        currency?: string,
        shippingAddress?: any,
        billingAddress?: any,
        customer?: any,
        payment_method_types?: string[],
        pricingResult?: any,
        discountCode?: string
    }) {
        return await ApiHelper.post(`/v1/payments/create-payment-intent`, payload)
    }

    async createPaymentIntent(paymentData: {
        amount?: number,
        currency: string,
        paymentMethodType: 'card' | 'oxxo' | 'spei',
        cardPaymentMethodId?: string,
        enableInstallments?: boolean,
        customer: {
            name: string,
            email: string,
            phone: string
        },
        items: any[],
        shippingAddress?: any,
        billingAddress?: any,
        discountCode?: string | null,
        userId?: string,
        tracking?: any
    }) {
        return await ApiHelper.post(`/v1/payments/create-payment-intent`, paymentData)
    }

    async confirmPayment(paymentData: {
        paymentIntentId: string,
        paymentMethodId?: string,
        customerInfo: any,
        shippingAddress?: any,
        billingAddress?: any,
        items: any[],
        total?: number,
        paymentMethodType: 'card' | 'oxxo' | 'spei',
        selectedInstallmentPlan?: null | {
            type: 'fixed_count',
            interval: 'month',
            count: number
        },
        discountCode?: string | null,
        userId?: string,
        tracking?: any
    }) {
        return await ApiHelper.post(`/v1/payments/confirm-payment`, paymentData)
    }

    async createPayPalOrder(paymentData: {
        customer: {
            name: string,
            email: string,
            phone: string
        },
        items: any[],
        shippingAddress?: any,
        billingAddress?: any,
        discountCode?: string | null,
        userId?: string,
        tracking?: any
    }) {
        return await ApiHelper.post(`/v1/payments/paypal/create-order`, paymentData)
    }

    async capturePayPalOrder(paymentData: {
        paypalOrderId: string,
        customerInfo: any,
        shippingAddress?: any,
        billingAddress?: any,
        items: any[],
        discountCode?: string | null,
        userId?: string,
        tracking?: any
    }) {
        return await ApiHelper.post(`/v1/payments/paypal/capture-order`, paymentData)
    }

    async getPayPalConfig() {
        return await ApiHelper.get(`/v1/payments/paypal/config`)
    }

    async CreateSaleFromPaymentIntent(payload: { paymentIntentId: string }) {
        return await ApiHelper.post(`/v1/sales/create-sale-from-payment-intent`, payload)
    }

    ////// stock control
    async getVariantsWithLowStock() {
        return await ApiHelper.get(`/v1/stock/get-low-stock-variants`)
    }

    async getCountries() {
        return await ApiHelper.get(`/v1/country/get`)
    }

    async getStates(countryId: string) {
        return await ApiHelper.get(`/v1/state/get-by-countryId/${countryId}`)
    }

    async getActiveMaterials(channel: 'catalog' | 'personalized' = 'catalog') {
        return await ApiHelper.get(`/v1/materials/active?channel=${channel}`)
    }

    async getActiveFrames() {
        return await ApiHelper.get(`/v1/frames/active`)
    }

    async getActiveMarialuisas() {
        return await ApiHelper.get(`/v1/marialuisas/active`)
    }

    async getMarialuisasForSize(width: number, height: number) {
        return await ApiHelper.get(`/v1/marialuisas/for-size?width=${width}&height=${height}`)
    }

    async quotePrice(data: any) {
        return await ApiHelper.post(`/v1/pricing/quote`, data)
    }

    async getActivePersonalizationTemplates() {
        return await ApiHelper.get(`/v1/personalization-templates/active`)
    }

    async getActiveHeroSlides() {
        return await ApiHelper.get(`/v1/hero-slides/active`)
    }

    async getPersonalizationPrintOptions() {
        return await ApiHelper.get(`/v1/personalization-templates/print-options`)
    }

    async uploadPersonalizationAsset(data: any) {
        return await ApiHelper.postWithFiles(`/v1/personalization-templates/upload`, data)
    }

    async getUserShippingAddresses() {
        return await ApiHelper.get(`/v1/user/shipping-addresses`)
    }

    async updateUserProfile(data: any) {
        return await ApiHelper.put(`/v1/user/profile`, data)
    }

    async createShippingAddress(data: any) {
        return await ApiHelper.post(`/v1/user/shipping-address`, data)
    }

    async updateShippingAddress(id: string, data: any) {
        return await ApiHelper.put(`/v1/user/shipping-address/${id}`, data)
    }

    async deleteShippingAddress(id: string) {
        return await ApiHelper.delete(`/v1/user/shipping-address/${id}`)
    }
}

export default new ApiProduct()
